import {plugin, defaultResolvers} from '@marvr/storyblok-rich-text-vue-renderer';
import {NodeTypes} from '@marvr/storyblok-rich-text-types';
import type {Component} from 'vue';
import {NuxtImg} from '#components';

import Tip from '@/storyblok/components/Tip.vue';
import HighlightedContent from '@/storyblok/components/HighlightedContent.vue';
import EmbedContent from '@/storyblok/components/EmbedContent.vue';
import TilesList from '@/storyblok/components/TilesList.vue';
import OrderedParagraphsList from '@/storyblok/components/OrderedParagraphsList.vue';
import TableContent from '@/storyblok/components/TableContent.vue';
import ImageWithLink from '@/storyblok/components/ImageWithLink.vue';
import FormEmbed from '@/storyblok/components/FormEmbed.vue';
import AbsoluteInternalLink from '@/components/AbsoluteInternalLink.vue';
import {hasRichTextClass} from '~/utils/applyRichTextImageMarks';

import type {
	EmbedContentStoryblok,
	HighlightedContentStoryblok,
	OrderedParagraphsListStoryblok,
	TilesListStoryblok,
	TipStoryblok,
	TableContentStoryblok,
	ImageWithLinkStoryblok,
	FormEmbedStoryblok,
} from '~/storyblok/types';

export default defineNuxtPlugin((nuxtApp) => {
	nuxtApp.vueApp.use(plugin({
		resolvers: {
			...defaultResolvers,
			[NodeTypes.LINK]: (fields) => {
				switch (fields.attrs.linktype) {
					case 'email':
						return h(
							'a',
							{href: `mailto:${fields.attrs.href}`},
							fields.text?.children ? String(fields.text.children) : '',
						);

					case 'asset':
					case 'url':
						return h(
							'a',
							{href: String(fields.attrs.href), target: String(fields.attrs.target)},
							fields.text?.children ? String(fields.text.children) : '',
						);
					default:
						return h(
							AbsoluteInternalLink as Component,
							{...fields.attrs, href: fields.attrs.href},
							{default: () => (fields.text?.children ? String(fields.text.children) : '')},
						);
				}
			},
			[NodeTypes.IMAGE]: (fields) => {
				const {class: markClass, ...attrs} = fields.attrs as typeof fields.attrs & {class?: string};
				// SB "text-center" on images is a styled mark; text-align does nothing on <img>.
				const centered = hasRichTextClass(markClass, 'text-center');

				return h(NuxtImg as Component, {
					...attrs,
					class: centered ? 'rounded-md mx-auto block' : 'rounded-md',
					provider: 'storyblok',
					loading: 'lazy',
				});
			},
			components: {
				EmbedContent: ({fields}) => h(EmbedContent, {blok: fields as EmbedContentStoryblok}),
				HighlightedContent: ({fields}) => h(HighlightedContent, {blok: fields as HighlightedContentStoryblok}),
				OrderedParagraphsList: ({fields}) => h(OrderedParagraphsList, {blok: fields as OrderedParagraphsListStoryblok}),
				Tip: ({fields}) => h(Tip, {blok: fields as TipStoryblok}),
				TilesList: ({fields}) => h(TilesList, {blok: fields as TilesListStoryblok}),
				TableContent: ({fields}) => h(TableContent, {blok: fields as TableContentStoryblok}),
				LinkedImage: ({fields}) => h(ImageWithLink, {blok: fields as ImageWithLinkStoryblok}),
				ImageWithLink: ({fields}) => h(ImageWithLink, {blok: fields as ImageWithLinkStoryblok}),
				FormEmbed: ({fields}) => h(FormEmbed, {blok: fields as FormEmbedStoryblok}),
			},
		},
	}));
});
